import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { WebSerialScaleService } from '../../../../services/web-serial-scale.service';
 
export interface FoamCupMeasureDialogData {
  cupWeight?: number;
  cupVolume?: number;
}
 
export interface FoamCupMeasureDialogResult {
  cupWeight?: number;
  cupVolume?: number;
}
 
@Component({
  selector: 'app-foam-cup-measure-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    DecimalPipe
  ],
  templateUrl: './foam-cup-measure-dialog.component.html',
  styleUrls: ['./foam-cup-measure-dialog.component.css']
})
export class FoamCupMeasureDialogComponent implements OnDestroy {
  cupWeight?: number;
  cupVolume?: number;
 
  // Scale reading state
  isMeasuringWeight = true; // Start measuring weight first
  isMeasuringVolume = false;
  scaleError?: string;
  private abortController?: AbortController;
 
  constructor(
    public dialogRef: MatDialogRef<FoamCupMeasureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FoamCupMeasureDialogData,
    private scaleService: WebSerialScaleService
  ) {
    // Pre-populate with existing values
    this.cupWeight = data.cupWeight;
    this.cupVolume = data.cupVolume;
   
    // Auto-start scale reading for cup weight (matches WPF behavior lines 487-513)
    this.startScaleReadingForWeight();
  }
 
  ngOnDestroy(): void {
    this.abortController?.abort();
  }
 
  get statusMessage(): string {
    if (this.isMeasuringWeight && !this.cupWeight && this.cupWeight !== 0) {
      return 'Waiting for scale reading…';
    } else if (this.scaleError) {
      return this.scaleError;
    } else if (!this.cupWeight && this.cupWeight !== 0) {
      return 'Please Weigh Foam Cup…';
    } else if (!this.cupVolume && this.cupVolume !== 0) {
      return 'Please Enter Cup Volume…';
    }
    return '';
  }
 
  get showStatusBanner(): boolean {
    return this.statusMessage !== '';
  }
 
  get resultDensity(): string {
    if (
      this.cupWeight !== undefined &&
      this.cupWeight !== null &&
      this.cupVolume !== undefined &&
      this.cupVolume !== null &&
      this.cupVolume > 0
    ) {
      const density = this.cupWeight / this.cupVolume;
      return density.toFixed(3);
    }
    return 'N/A';
  }
 
  get isValid(): boolean {
    return (
      this.cupWeight !== undefined &&
      this.cupWeight !== null &&
      this.cupVolume !== undefined &&
      this.cupVolume !== null &&
      this.cupVolume > 0 &&
      !this.isMeasuringWeight
    );
  }
 
  /**
   * Matches WPF lines 487-513: MeasureFoamCup() method
   * Automatically starts reading cup weight from scale, then prompts for volume
   */
  private async startScaleReadingForWeight(): Promise<void> {
    this.isMeasuringWeight = true;
    this.isMeasuringVolume = false;
    this.scaleError = undefined;
    this.abortController = new AbortController();
 
    try {
      const result = await this.scaleService.readWeightFromGrantedPort(this.abortController.signal);
     
      if (result.success && result.stable) {
        this.cupWeight = result.weight;
        // After cup weight is captured, focus on volume input
        this.isMeasuringWeight = false;
        this.isMeasuringVolume = true;
      } else if (result.errorMessage) {
        this.scaleError = result.errorMessage;
        this.isMeasuringWeight = false;
      }
    } catch (error) {
      console.error('Scale reading error:', error);
      this.scaleError = error instanceof Error ? error.message : 'Unknown scale error';
      this.isMeasuringWeight = false;
    }
  }
 
  /**
   * Retry scale reading for cup weight
   */
  async retryScaleReading(): Promise<void> {
    this.abortController?.abort();
    await this.startScaleReadingForWeight();
  }
 
  onCancel(): void {
    this.abortController?.abort();
    this.dialogRef.close();
  }
 
  onSave(): void {
    if (this.isValid) {
      const result: FoamCupMeasureDialogResult = {
        cupWeight: this.cupWeight,
        cupVolume: this.cupVolume
      };
      this.dialogRef.close(result);
    }
  }
}